import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGetProductsQuery } from "../api/productApi";
import { TransformedProduct } from "../types/productApiTypes";

/**
 * A custom hook to determine the list of related product IDs and the appropriate title
 * based on a priority system (Upsells > Cross-sells > Same Category).
 *
 * @param product The main product for which to find related items.
 * @param priority The primary type of relation to show ('upsell' or 'cross_sell').
 * @returns An object containing the final array of product IDs and the section title.
 */
export const useRelatedProducts = (
  product: TransformedProduct | null | undefined,
  priority: "upsell" | "cross_sell"
) => {
  const { t } = useTranslation();

  // --- Determine Primary and Secondary IDs based on priority ---
  const primaryIds =
    priority === "upsell"
      ? product?.upsellProductIds || []
      : product?.crossSellProductIds || [];
  const secondaryIds =
    priority === "upsell"
      ? product?.crossSellProductIds || []
      : product?.upsellProductIds || [];

  const primaryTitle =
    priority === "upsell"
      ? t("relatedProducts.upsellsTitle")
      : t("relatedProducts.crossSellsTitle");
  const secondaryTitle =
    priority === "upsell"
      ? t("relatedProducts.crossSellsTitle")
      : t("relatedProducts.upsellsTitle");

  // --- Logic for Fallback to Category Products ---
  const primaryCategoryId = product?.enrichedCategories?.[0]?.id;
  const hasPrimary = primaryIds.length > 0;
  const hasSecondary = secondaryIds.length > 0;

  // Conditionally skip the category fetch if we don't need it.
  const skipCategoryFetch =
    !product || hasPrimary || hasSecondary || !primaryCategoryId;

  const { data: categoryProductsResult } = useGetProductsQuery(
    {
      category: [primaryCategoryId!], // The "!" is safe because of the skip condition
      limit: 6, // Fetch one extra to easily exclude the current product
    },
    {
      skip: skipCategoryFetch,
    }
  );

  // --- Memoize the final result to prevent re-calculations on every render ---
  return useMemo(() => {
    if (!product) {
      return {
        productIds: [],
        title: t("relatedProducts.title"), // Generic fallback title
      };
    }

    // Priority 1: Use primary IDs if they exist.
    if (hasPrimary) {
      return { productIds: primaryIds, title: primaryTitle };
    }

    // Priority 2: Use secondary IDs if they exist.
    if (hasSecondary) {
      return { productIds: secondaryIds, title: secondaryTitle };
    }

    // Priority 3: Use category products as a fallback.
    if (categoryProductsResult?.products) {
      const ids = categoryProductsResult.products
        .filter((p) => p.id !== product.id) // Exclude the current product
        .slice(0, 5) // Ensure we only have a max of 5
        .map((p) => p.id); // Return just the IDs

      return {
        productIds: ids,
        title: t("relatedProducts.title"), // Use generic title for category fallback
      };
    }

    // Final fallback: Return an empty array.
    return { productIds: [], title: t("relatedProducts.title") };
  }, [
    product,
    primaryIds,
    secondaryIds,
    hasPrimary,
    hasSecondary,
    categoryProductsResult,
    primaryTitle,
    secondaryTitle,
    t,
  ]);
};
